<script>
import { getAssetTypes, saveApplication } from '@/app/api';
import {mapActions, mapState} from "vuex";
import ApplicationConfig from "../config.json";
import {isArray, isObject} from "@incodeapps/js-utilities";
import { throttle } from 'throttle-debounce';
import ApplicationValidator from "ApplicationValidator";
import Program from "../Program.vue";
import checkMark from "@/assets/img/icons/check-mark.svg";
import exclamationMark from "@/assets/img/icons/exclamation-mark.svg";
import garbageCan from "@/assets/img/icons/garbage-can.svg";

export default {
	filters: {
		formatMoney(value) {
			return new Intl.NumberFormat('en-US', {minimumFractionDigits: 2,}).format(value);
		},
	},
	components: {
		Program
	},
	computed: {
		...mapState({
			locale: state => state.app.locale,
			optionSets: state => state.app.options
		}),
		formsJson() {
			return ApplicationConfig;
		}
	},
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
		goToStep: {
			type: Function,
			default: () => {}
		},
		type: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			//icons
			checkMark: checkMark,
			exclamationMark: exclamationMark,
			garbageCan: garbageCan,
			//values
			assetTypes: {},
			assetSubTypes: {},
			enableOnly: {},
			errors: {},
			submitting: false,
			isLoading: true,
			programType: '',
			applicant: {},
			coApps: [],
			equipments: [],
			selectedApp: {},
			selectedAppIndex: '',
			app: null,
			vModels: {
				program: {
					financedAmount: ''
				}
			},
			optionals: [
				'mobilePhone',
				'sin',
			]
		};
	},
	async mounted() {
		this.saveLoading(true);
		this.app = this.application;
		this.applicant = this.makeApplicantObject(this.getApplicant());
		if (this.app.application.equipments) {
			this.equipments = this.app.application.equipments;
		}
		this.vModels.program = this.app.application.program;
		this.coApps = this.app.application.coApps;
		await this.getOptions(this.app.application.program.program.ProgramID ?? this.app.application.program.program.programId);
		this.programType = this.type.charAt(0).toUpperCase() + this.type.slice(1);
		const equipmentInput = document.getElementById('equipment-type');
		if (equipmentInput) {
			equipmentInput.focus();
		}
		this.saveLoading(false);
	},
	methods: {
		...mapActions([
			'saveLoading',
			'showErrorAlert'
		]),
		getApplicant() {
			return this.app?.application;
		},
		makeApplicantObject(data) {
			return {
				address1: data?.address1,
				address2: data?.address2,
				city: data?.city,
				country: data?.country,
				dob: data?.dob,
				email: data?.email ,
				employer: data?.employer,
				employmentTerm: data?.employmentTerm,
				employmentTitle: data?.employmentTitle,
				firstName: data?.firstName ?? '',
				grossIncome: data?.grossIncome,
				lastName: data?.lastName ?? '',
				linkPurpose: data?.linkPurpose,
				linkSentOn: data?.linkSentOn,
				middleInitial: data?.middleInitial,
				postal: data?.postal,
				primaryPhone: data?.primaryPhone,
				province: data?.province,
				titleOwner: data?.titleOwner
			}
		},
		async removeEquipment(index) {
			this.saveLoading(true);
			const newEquipment = [];
			for (const equipment in this.equipments) {
				if (Number(equipment) !== index) {
					newEquipment.push(this.equipments[equipment]);
				}
			}
			await this.saveApp(newEquipment)
			this.saveLoading(false);
		},
		async addEquipment() {
			if (this.assetTypes?.[this.vModels.equipmentType]?.en) {
				this.saveLoading(true);
				const equipmentInput = document.getElementById('equipment-type');
				equipmentInput.focus();
				let fields = this.getForm('addAsset');
				this.equipments.push({
					equipmentType: this.assetTypes[this.vModels.equipmentType]?.['en'] || '',
					brand: this.vModels.brand?.toUpperCase() || '',
					model: this.vModels.model?.toUpperCase() || '',
					code: this.vModels.equipmentType
				})
				Object.keys(fields).forEach(element => this.vModels[element] = '');
				await this.saveApp(this.equipments)
				this.saveLoading(false);
			}
		},
		async getOptions(programId) {
			this.saveLoading(true);
			let result = await getAssetTypes(programId || 101).catch(async error => {
				this.errorAlert = await this.showErrorAlert(error);
				this.saveLoading(false);
			});
			this.assetTypes = result.assetTypes;
			if (this.assetTypes) {
				this.vModels.equipmentType = this.assetTypes[Object.keys(this.assetTypes)[0]]['code']
			}
			this.isLoading = false;
			this.saveLoading(false);
		},
		async saveApp(newEquipment) {
			this.saveLoading(true);
			await saveApplication({
				id: this.app.application.id,
				equipments: newEquipment
			});
			this.app = await this.getApplication();
			this.equipments = this.app.application.equipments;
			this.saveLoading(false);
		},
		onSubmit(e) {
			this.saveLoading(true);
			this.addEquipment();
			this.saveLoading(false);
		},
		checkApplicant(applicant) {
			let result = true;
			if (!applicant) {
				result = false;
			}
			let required = [
				'address1',
				'city',
				'dob',
				'employer',
				'employmentTerm',
				'employmentTitle',
				'firstName',
				'grossIncome',
				'lastName',
				'postal',
				'primaryPhone',
				'province',
				'titleOwner',
			]
			for (const field of required) {
				if ((applicant?.[field]?.trim?.() || '-') === '-') {
					result = false;
					break;
				}
			}
			return result;
		},
		getForm(key) {
			if (!this.formsJson) {
				return;
			}
			return this.formsJson[key];
		},
		getLabel(fieldName) {
			if (/^row/.test(fieldName)) {
				return '';
			}
			if (this.countryValue === 'USA') {
				switch (fieldName) {
					case 'province':
						return this.$i18n.t('newApplication.state');
					case 'postal':
						return this.$i18n.t('newApplication.zipCode');
					case 'prevProvince':
						return this.$i18n.t('newApplication.prevState');
					case 'prevPostal':
						return this.$i18n.t('newApplication.prevZipCode');
				}
			}
			return this.$i18n.t('newApplication.' + fieldName);
		},
		checkForOptions(name, data) {
			let val;
			if (['province', 'prevProvince'].includes(name)) {
				val = this.optionSets.provinces
			} else if (name === 'coborrowerRelationship') {
				val = this.optionSets['relationships'];
			} else if (Object.keys(this.optionSets ?? {}).includes(name)) {
				val = this.optionSets[name];
			}
			if (isObject(val) || isArray(val)) {
				this.$set(this.options, name, val);
				if (isObject(data)) {
					data.options = this.options[name];
				}
				if (this.vModels && this.vModels[name] !== undefined) {
					let vModelVal = this.vModels && this.vModels[name];
					let exist;
					if (isArray(val)) {
						exist = val.filter(item => item === vModelVal);
					} else {
						exist = Object.keys(val).filter(item => item === vModelVal);
					}
					if (!exist.length) {
						let firstOptionVal = this.getFirstOption(val);
						this.$set(this.vModels, name, firstOptionVal);
						this.manuallySetSelectValue(name, this.vModels[name]);
					}
				}
			}
			this.$nextTick(function () {
				if (this.vModels && this.vModels[name] !== undefined) {
					this.manuallySetSelectValue(name, this.vModels[name]);
				}
			});
			return data;
		},
		getFirstOption(options) {
			let first;
			Object.keys(options).forEach(optionKey => {
				if (first) {
					return;
				}
				first = optionKey;
			});
			return first;
		},
		manuallySetSelectValue(fieldName, val) {
			// Hack to make the fields use a default value
			let elm = document.querySelector(`select[name=${fieldName}]`);
			if (elm) {
				elm.childNodes.forEach(child => {
					if (child.value == val) {
						child.selected = true;
					}
				});
				if (val !== elm.value) {
					elm.value = val;
					elm.dispatchEvent(new CustomEvent('change', elm));
				}
			}
		},
		getMask(name, data) {
			if (['postal', 'prevPostal'].includes(name)) {
				switch (this.countryValue) {
					case 'US':
					case 'USA':
						return ['#####', '#####-####'];
					default:
					case 'CA':
					case 'CAN':
						return 'A#A #A#';
				}
			}
			return data.mask;
		},
		onChange: throttle(100, function (val) {
			// for MS Edge
			if (navigator.userAgent.indexOf('Edge') !== -1) {
				if (window.event?.type === 'change' && this.vModels?.[window?.event?.target?.name] !== val) {
					this.$set(this.vModels, window.event.target.name, val);
				}
			}
			this.$nextTick(() => {
				if (!this.validated && !this.validateAll) {
					return;
				}
				this.errors = this.validateRequiredFields(this.vModels);
			});
		}),
		validateRequiredFields(fields, scroll = false) {
			let response = false;
			let schema = Object.assign({}, this.joiSchema);
			let errors = ApplicationValidator.validate(fields, schema, this.locale);
			if (errors) {
				Object.keys(errors).forEach(i => {
					if (!isObject(response)) {
						response = {};
					}
					response[errors[i].key] = errors[i].message;
				});
			}
			this.validated = true;
			if (scroll) {
				setTimeout(function () {
					let invalids = document.querySelectorAll(".was-validated, .md-invalid");
					if (invalids && invalids[0]) {
						this.$scrollTo(invalids[0]);
					}
				}.bind(this), 150);
			}
			return response;
		},
	}
};
</script>

<template>
	<div class="add-applicant-template">
		<div class="container-xl">
			<div class="row">
				<div class="col-24">
					<div class="template__page-title">
						<h1>Equipment</h1>
					</div>
				</div>
			</div>
		</div>
		<form @submit.prevent="onSubmit" novalidate>
			<div class="container-xl template--vh">
				<div class="row">
					<div class="col-lg-8">
						<Program :programType="programType"
						         :financedAmount="vModels.program.isStepLoan ? vModels.program.amount : vModels.program.financedAmount || vModels.program.totalPayments"
						         :periodPayment="vModels.program.isStepLoan ? vModels.program.periodPaymentPhaseOne : vModels.program.periodPayment"
						         :initialPayment="vModels.program.initialPayment ?? null"
						         :initialPaymentWithTax="vModels.program.initialPaymentWithTax ?? null"
						         :equipmentPrice="vModels.program.equipmentPrice ?? null"
						         :leaseTermMonths="vModels.program.leaseTermMonths ?? null"
						         :serviceTotal="vModels.program.serviceTotal ?? null"
						         :totalPaymentsWithTax="vModels.program.totalPaymentsWithTax ?? null"
						         :apr="vModels.program.apr ?? null" />
						<div style="cursor: pointer;"
						     @click="goToStep({ name: 'applicants', query: { select: 'main' }})" class="tffi-card tffi-card-secondary-card theming primary-color person">
							<div class="d-none- tffi-card-secondary-card__left-area">
								<div class="tffi-card-secondary-card__left-area__status-indicator">
									<div v-if="checkApplicant(applicant)" class="status-accepted">
										<img :src="checkMark" alt="">
									</div>
									<div v-else class="status-denied">
										<img :src="exclamationMark" alt="">
									</div>
								</div>
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Applicant</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>{{ applicant.firstName + ' ' + (applicant.middleInitial ?? '') + ' ' +
									applicant.lastName }}</p>
									<p>{{ applicant.email }}</p>
								</div>
							</div>
						</div>
						<div style="cursor: pointer;"
						     @click="goToStep({ name: 'applicants', query: { select: index.toString() }})"
						     v-if="coApps" v-for="(coApp, index) in this.coApps" class="tffi-card tffi-card-secondary-card person">
							<div class="d-none- tffi-card-secondary-card__left-area">
								<div class="tffi-card-secondary-card__left-area__status-indicator">
									<div v-if="checkApplicant(coApps[index])" class="status-accepted">
										<img :src="checkMark" alt="">
									</div>
									<div v-else class="status-denied">
										<img :src="exclamationMark" alt="">
									</div>
								</div>
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Co-Applicant</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>{{ (coApp.firstName ?? '') + ' ' + (coApp.middleInitial ?? '') + ' ' + (coApp.lastName ?? '') }}</p>
									<p>{{ coApp.email }}</p>
								</div>
							</div>
						</div>
						<div v-if="equipments" v-for="(equipment, index) in this.equipments"
						     class="tffi-card tffi-card-secondary-card">
							<div class="tffi-card-secondary-card__left-area">
							</div>
							<div class="tffi-card-secondary-card__right-area">
								<div class="tffi-card-secondary-card__right-area__title-area">
									<h2>Equipment</h2>
								</div>
								<div class="tffi-card-secondary-card__right-area__content-area">
									<p>Type: {{ equipment['equipmentType'] }}</p>
									<p>Brand: {{ equipment['brand'] }}</p>
									<p>Model: {{ equipment['model'] }}</p>
								</div>
								<button type="button" @click="removeEquipment(index)"
								        class="tffi-card-secondary-card__right-area__trash-area">
									<img :src="garbageCan" alt="">
								</button>
							</div>
						</div>
						<div class="template--section-divider"></div>
						<div class="template--section-divider"></div>
					</div>
					<div class="col-lg-16">
						<div class="tffi-card tffi-card-main-card" v-if="equipments.length >= 4">
							<div class="row">
								<div class="col-24">
									<p>Maximum number of equipments reached</p>
								</div>
							</div>
						</div>
						<div v-if="equipments.length < 4" class="tffi-card tffi-card-main-card">
							<div class="row row-cols-lg-2">
								<template v-for="(fieldData, fieldName) in getForm('addAsset')">
									<div :class="fieldData.class">
										<div class="d-flex flex-column">
											<form-field v-bind="checkForOptions(fieldName, fieldData)" v-model="vModels[fieldName]"
											            :name="fieldName" :label="getLabel(fieldName)" :error="errors[fieldName]" :locale="locale"
											            :mask="getMask(fieldName, fieldData)" :enable-only="enableOnly[fieldName]" :options="assetTypes"
											            bootstrap @change="onChange"></form-field>
										</div>
									</div>
								</template>
								<div class="col-lg-12 d-inline-flex justify-content-end align-items-center">
									<button class="tffi-btn tffi-btn--solid theming accent-color add-equipment-btn px-5" type="submit">
										Add
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</form>
	</div>
</template>

<style lang="scss" scoped>
.tffi-card-secondary-card.person {
	&:hover {
		border: 2px solid #C18F14;
	}
}
</style>
